import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { Spinner } from '@/components/ui'
import { updateMandat, createMandat, deleteMandat, emptyMandat, updateMandatWithProprietaires } from './mandatsSlice'
import { updateBien } from '../biens/biensSlice'
import { useBienContext } from '../biens/BienContext'

import CloseMandatForm from './CloseMandatForm'
import EditMandatForm from './EditMandatForm'
import { FORM_TYPES, MandatFormProvider } from './MandatFormContext'

import useFormConfirmation from "@/hooks/useFormConfirmation"

export default ({formType = FORM_TYPES.EDIT, onClose = () => {}, onSuccess = () => {}}) => {
  const dispatch = useDispatch()
  const { bien } = useBienContext()
  const mandat = useSelector((state) => state.mandats.inEdition || state.mandats.current || emptyMandat)
  const errors = useSelector((state) => state.mandats.requestStatus.errors)
  const isCreating = useSelector((state) => state.mandats.loadingStatus.create)
  const isUpdating = useSelector((state) => state.mandats.loadingStatus.update)
  const bienLoading = useSelector(state => state.biens.loadingStatus.update)
  const bienErrors = useSelector(state => state.biens.requestStatus.errors)

  const isLoading = isCreating || isUpdating
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [ showConfirmDialog, setShowConfirmDialog ] = useState(false)

  const { handleSubmit, 
    watch,
    setValue, getValues, reset, 
    control, formState: { errors: formErrors }
  } = useForm({
    defaultValues: {
      ...mandat,
      proprietaires: mandat.proprietaires_signataires
    },
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const onReset = () => {
    reset(mandat)
  }

  /**
   * Récupère le message d'erreur pour un champ donné
   * Combine les erreurs de validation côté client et les erreurs API
   *
   * @param {string} fieldName - Nom du champ
   * @returns {string} Message d'erreur ou chaîne vide
   */
  const getErrorMessage = (fieldName) =>
    formErrors?.[fieldName]?.message || errors?.[fieldName] || ''

  /**
   * Détermine si un champ a une erreur
   *
   * @param {string} fieldName - Nom du champ
   * @returns {boolean} true si le champ a une erreur, false sinon
   */
  const hasError = (fieldName) =>
    !!formErrors?.[fieldName] || !!errors?.[fieldName]

  // useFormConfirmation feature
  // ajoute les states, le useEffect pour surveiller l'état du formulaire
  // et s'assurer s'il a été modifié ou pas pour afficher une boîte de 
  // confirmation lorsque le bouton "Annuler" est cliqué.
  const {
    handleFormCancel,
    setInitialLoadComplete,
    setInitialValues,
    setFormIsDirty,
    FormConfirmDialog
  } = useFormConfirmation({
    watch,
    getValues,
    onReset,
    onClose
  })

  // Synchroniser le formulaire lorsque mandat change 
  useEffect(() => {
    // Réinitialiser complètement le formulaire avec les nouvelles valeurs
    reset({
      ...mandat,
      proprietaires: mandat.proprietaires_signataires || [],
      cle_dispo: bien.cle_dispo,
      remise_cles_file: bien.remise_cles_file
    })
    
    setInitialValues(getValues()) 
    setInitialLoadComplete(true)
    setFormIsDirty(false) // Réinitialiser l'état dirty après le chargement initial
  }, [mandat, bien, reset, formType, getValues, setInitialValues])

  // Effet pour détecter les changements dans l'état de chargement
  useEffect(() => {
    if (isSubmitting && !isUpdating && !isCreating && !bienLoading) {
      setIsSubmitting(false)
      const hasErrors = (errors && Object.keys(errors).length > 0) || 
                        (bienErrors && Object.keys(bienErrors).length > 0)
      if (!hasErrors) {
        onReset()
        onSuccess()
      }
    }
  }, [isUpdating, isCreating, bienLoading, errors, bienErrors, isSubmitting, onSuccess, dispatch])

  /**
   * Gère la soumission du formulaire
   * Dispatche l'action appropriée (create ou update) selon si le mandat existe déjà
   *
   * @param {Object} formData - Données du formulaire
   */
  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true)

      const bienData = {
        cle_dispo: formData.cle_dispo,
        remise_cles_file: formData.remise_cles_file
      }
      const mandatData = { ...formData }
      delete mandatData.cle_dispo
      delete mandatData.remise_cles_file
      delete mandatData.proprietaires

      // Étape 1 : Créer/mettre à jour le mandat
      if (mandat.id) {
        if(formType === FORM_TYPES.EDIT && !mandat.date_fin) {
          await dispatch(updateMandatWithProprietaires({ id: mandat.id, bien_id: bien.id, 
            mandat: mandatData,
            proprietaires: formData.proprietaires.map(p => ({ 
            contact_id: p.id || p.contact_id 
          }))

          }))
        } else {
          await dispatch(updateMandat({bien_id: bien.id, id: mandat.id, data: mandatData, formType}))
        }
      } else {
        await dispatch(createMandat({data: mandatData, bien_id: bien.id, formType}))
      }

      // Étape 2 : Mettre à jour le bien (fichiers et cle_dispo)
      if (bienData.remise_cles_file !== undefined || bienData.cle_dispo !== bien.cle_dispo) {
        await dispatch(updateBien({ 
          id: bien.id, 
          bienData: bienData 
        }))
      }

    } catch (error) {
      setIsSubmitting(false)
    }
  }
 
  const handleDelete = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setShowConfirmDialog(true)
  }

  const confirmDelete = async () => {
    setIsSubmitting(true)
    await dispatch(deleteMandat({bien_id: bien.id, id: mandat.id}))
    setIsSubmitting(false)
    setShowConfirmDialog(false)
    onSuccess()
  }
  
  const cancelDelete = () => {
    setShowConfirmDialog(false)
  }

  const contextValues = {
    hasError,
    getErrorMessage,
    getValues,
    control,
    mandat,
    isLoading,
    formErrors,
    errors,
    watch
  }

  const getFormTitle = () => {
    switch(formType) {
      case FORM_TYPES.CLOSE:
        return "Terminer le mandat"
      case FORM_TYPES.EDIT:
      default:
        return "Modifier le mandat"
    }
  }

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">{getFormTitle()}</h2>
        </div>

        <div className="card bg-base-100 border border-base-200 rounded-none mb-4 min-h-[450px] max-h-[calc(80vh-150px)] overflow-y-auto">
          <div className="p-4">
            <div className="card-body p-4">
              <MandatFormProvider {...contextValues}>
                {(() => {
                  switch(formType) {
                    case FORM_TYPES.CLOSE:
                      return <CloseMandatForm />
                    case FORM_TYPES.EDIT:
                    default:
                      return <EditMandatForm />
                  }
                })()}
              </MandatFormProvider>
            </div>
          </div>
        </div>

        <div className="flex justify-between">
          <button
            type="button"
            className="btn btn-outline"
            onClick={handleFormCancel}
          >
            Annuler
          </button>

          <div className="space-x-2">
            <button
              className={`btn btn-secondary text-white p-2 mt-4`}
              disabled={isLoading}
              onClick={handleDelete}
            >
              Supprimer
            </button>

            <button
              type="submit"
              className={`btn ${isLoading ? 'loading' : ''} btn-primary text-white p-2 mt-4`}
              disabled={isLoading}
            >
              {isLoading ? <Spinner/> : 'Enregistrer'}
            </button>
          </div>

        </div>
      </form>
      <FormConfirmDialog />
      {(showConfirmDialog && mandat) && (
        <dialog open className="modal">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Confirmer la suppression</h3>
            <p className="py-4">Êtes-vous sûr de vouloir supprimer ce mandat ?</p>
            <p className="py-4 text-center">
              Mandat de <span className="capitalize">{ mandat.type_mandat }</span>
              {mandat.numero && <span> n° {mandat.numero}</span>}
            </p>
            <div className="modal-action">
              <button onClick={confirmDelete} className="btn btn-secondary">Supprimer</button>
              <button onClick={cancelDelete} className="btn btn-outline">Annuler</button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  )
}